/**
 * Fiche d'une demande de devis.
 *
 * Elle est faite pour être ouverte pendant un appel : les coordonnées sont en
 * haut, cliquables, et le reste — projet, provenance, notes, historique —
 * s'enchaîne dans l'ordre où la conversation en a besoin.
 */
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import HistoryIcon from "@mui/icons-material/History";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhoneIcon from "@mui/icons-material/Phone";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { useState } from "react";
import { Link as LienRoutage, useParams } from "react-router";
import {
  type EvenementStatut,
  LIBELLES_BUDGET,
  LIBELLES_DELAI,
  LIBELLES_PROJET,
  LIBELLES_STATUT_LEAD,
  type Lead,
  STATUTS_LEAD,
  type StatutLead,
  formaterHorodatage,
  formaterTelephone,
  libelle,
  useConvertirLead,
  useLead,
  useSuivreLead,
} from "../composants/LeadsDonnees";
import { PuceStatut } from "../composants/LeadsStatut";

function Carte({
  titre,
  action,
  children,
}: { titre: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h4" component="h2">
            {titre}
          </Typography>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

/** Ligne « intitulé / valeur », alignée en colonnes sur les écrans larges. */
function Ligne({ intitule, children }: { intitule: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
        gap: { xs: 0.25, sm: 2 },
        py: 0.75,
      }}
    >
      <Typography variant="body2" color="text.secondary" component="dt">
        {intitule}
      </Typography>
      <Box component="dd" sx={{ m: 0 }}>
        {typeof children === "string" ? (
          <Typography variant="body1">{children}</Typography>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}

export function LeadFiche() {
  const parametres = useParams();
  const id = Number(parametres.id);

  const fiche = useLead(id);
  const suivi = useSuivreLead();
  const conversion = useConvertirLead();

  // Le brouillon de notes ne remplace la valeur du serveur qu'une fois la
  // saisie commencée : tant qu'il vaut `null`, un rechargement en arrière-plan
  // met la fiche à jour sans écraser ce que personne n'a encore tapé.
  const [brouillon, setBrouillon] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState(false);

  if (fiche.isPending) {
    return (
      <Stack spacing={3}>
        <Skeleton variant="text" height={56} />
        <Skeleton variant="rounded" height={220} />
        <Skeleton variant="rounded" height={220} />
      </Stack>
    );
  }

  if (fiche.isError) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">Cette demande n'a pas pu être chargée. {fiche.error.message}</Alert>
        <Button component={LienRoutage} to="/leads" startIcon={<ArrowBackIcon />}>
          Retour à la liste
        </Button>
      </Stack>
    );
  }

  const { lead, historique } = fiche.data;
  const notesServeur = lead.notes;
  const notes = brouillon ?? notesServeur;
  const notesModifiees = brouillon !== null && brouillon !== notesServeur;

  function enregistrerNotes() {
    suivi.mutate({ id, notes }, { onSuccess: () => setBrouillon(null) });
  }

  function changerStatut(statut: StatutLead) {
    suivi.mutate({ id, statut });
  }

  return (
    <Stack spacing={3}>
      <Button
        component={LienRoutage}
        to="/leads"
        startIcon={<ArrowBackIcon />}
        sx={{ alignSelf: "flex-start" }}
      >
        Retour aux demandes
      </Button>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h1" component="h1">
            {lead.nom}
          </Typography>
          <Typography color="text.secondary">
            {lead.communeTexte} — reçue le {formaterHorodatage(lead.createdAt)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PuceStatut statut={lead.statut} taille="medium" />
          {lead.clientId === null ? (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAddIcon />}
              onClick={() => setConfirmation(true)}
              disabled={conversion.isPending}
            >
              {conversion.isPending ? "Conversion…" : "Convertir en client"}
            </Button>
          ) : (
            <Chip color="success" variant="outlined" label={`Client n° ${lead.clientId}`} />
          )}
        </Box>
      </Box>

      {conversion.isError && (
        <Alert severity="error" onClose={() => conversion.reset()}>
          La conversion a échoué. {conversion.error.messageGlobal}
        </Alert>
      )}

      {suivi.isError && (
        <Alert severity="error" onClose={() => suivi.reset()}>
          La modification n'a pas pu être enregistrée. {suivi.error.messageGlobal}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
          alignItems: "start",
        }}
      >
        <Stack spacing={3}>
          <Carte titre="Coordonnées">
            <Box component="dl" sx={{ m: 0 }}>
              <Ligne intitule="Téléphone">
                {/* Le suivi se fait au téléphone, souvent depuis un chantier :
                    le numéro doit se composer d'un geste, sans recopie. */}
                <Button
                  component="a"
                  href={`tel:${lead.telephone}`}
                  startIcon={<PhoneIcon />}
                  variant="outlined"
                  size="small"
                >
                  {formaterTelephone(lead.telephone)}
                </Button>
              </Ligne>

              <Ligne intitule="Email">
                <Button
                  component="a"
                  href={`mailto:${lead.email}`}
                  startIcon={<EmailIcon />}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: "none" }}
                >
                  {lead.email}
                </Button>
              </Ligne>

              <Ligne intitule="Commune">{lead.communeTexte}</Ligne>
            </Box>
          </Carte>

          <Carte titre="Projet">
            <Box component="dl" sx={{ m: 0 }}>
              <Ligne intitule="Type de projet">{libelle(LIBELLES_PROJET, lead.typeProjet)}</Ligne>

              {lead.pieces.length > 0 && (
                <Ligne intitule="Pièces concernées">
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {lead.pieces.map((piece) => (
                      <Chip key={piece} size="small" label={piece} />
                    ))}
                  </Box>
                </Ligne>
              )}

              {lead.surface !== null && <Ligne intitule="Surface">{`${lead.surface} m²`}</Ligne>}

              <Ligne intitule="Budget envisagé">{libelle(LIBELLES_BUDGET, lead.budget)}</Ligne>
              <Ligne intitule="Délai souhaité">{libelle(LIBELLES_DELAI, lead.delai)}</Ligne>

              {lead.message !== "" && (
                <Ligne intitule="Message">
                  {/* `pre-wrap` conserve les retours à la ligne saisis au
                      formulaire : les recoller en un pavé rend illisible une
                      description de chantier faite en liste. */}
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>{lead.message}</Typography>
                </Ligne>
              )}
            </Box>
          </Carte>

          <Carte titre="Provenance">
            <Origine lead={lead} />
          </Carte>
        </Stack>

        <Stack spacing={3}>
          <Carte titre="Suivi">
            <TextField
              select
              fullWidth
              label="Statut"
              value={lead.statut}
              onChange={(evenement) => changerStatut(evenement.target.value as StatutLead)}
              disabled={suivi.isPending}
              helperText="Chaque changement est daté et attribué dans l'historique."
            >
              {STATUTS_LEAD.map((statut) => (
                <MenuItem key={statut} value={statut}>
                  {LIBELLES_STATUT_LEAD[statut]}
                </MenuItem>
              ))}
            </TextField>
          </Carte>

          <Carte titre="Notes">
            <Stack spacing={1.5}>
              <TextField
                multiline
                minRows={5}
                fullWidth
                label="Notes internes"
                placeholder="Accès, disponibilités, points à vérifier avant le devis…"
                value={notes}
                onChange={(evenement) => setBrouillon(evenement.target.value)}
              />
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button onClick={() => setBrouillon(null)} disabled={!notesModifiees}>
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  onClick={enregistrerNotes}
                  disabled={!notesModifiees || suivi.isPending}
                >
                  {suivi.isPending ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </Box>
            </Stack>
          </Carte>

          <Carte titre="Historique">
            <Historique evenements={historique} createdAt={lead.createdAt} />
          </Carte>
        </Stack>
      </Box>

      <Dialog open={confirmation} onClose={() => setConfirmation(false)}>
        <DialogTitle>Convertir cette demande en client ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Un client sera créé avec les coordonnées de {lead.nom}, et la demande passera au statut
            « Gagné ». La demande reste consultable et conserve son historique.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setConfirmation(false);
              conversion.mutate(id);
            }}
          >
            Convertir
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * Page d'origine et campagne. Ces champs disent d'où vient la demande, donc ce
 * qui a fonctionné : les afficher vides plutôt que les masquer permet de voir
 * qu'une demande est venue en direct, et non que l'information manque.
 */
function Origine({ lead }: { lead: Lead }) {
  const campagne = [lead.utmSource, lead.utmMedium, lead.utmCampaign].filter(
    (valeur) => valeur !== "",
  );

  return (
    <Box component="dl" sx={{ m: 0 }}>
      <Ligne intitule="Page d'origine">
        <Typography variant="body1" component="code" sx={{ fontFamily: "monospace" }}>
          {lead.pageSource}
        </Typography>
      </Ligne>

      <Ligne intitule="Campagne">
        {campagne.length === 0 ? (
          <Typography color="text.secondary">Accès direct, sans campagne identifiée</Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {lead.utmSource !== "" && <Chip size="small" label={`source : ${lead.utmSource}`} />}
            {lead.utmMedium !== "" && <Chip size="small" label={`support : ${lead.utmMedium}`} />}
            {lead.utmCampaign !== "" && (
              <Chip size="small" label={`campagne : ${lead.utmCampaign}`} />
            )}
          </Box>
        )}
      </Ligne>

      {lead.userAgent !== "" && (
        <Ligne intitule="Navigateur">
          <Typography variant="body2" color="text.secondary">
            {lead.userAgent}
          </Typography>
        </Ligne>
      )}
    </Box>
  );
}

/**
 * Historique des changements de statut, du plus récent au plus ancien :
 * l'information la plus utile en ouvrant la fiche est le dernier mouvement,
 * pas le premier. L'API, elle, renvoie l'ordre chronologique — c'est le bon
 * ordre pour un journal, et l'inverser ici ne coûte rien.
 */
function Historique({
  evenements,
  createdAt,
}: {
  evenements: EvenementStatut[];
  createdAt: string;
}) {
  return (
    <Stack spacing={2}>
      {evenements.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Aucun changement de statut pour l'instant.
        </Typography>
      )}

      {[...evenements].reverse().map((evenement) => (
        <Box key={evenement.id} sx={{ display: "flex", gap: 1.5 }}>
          <HistoryIcon fontSize="small" sx={{ color: "text.secondary", mt: 0.25 }} aria-hidden />
          <Box>
            <Typography variant="body2">
              {libelle(LIBELLES_STATUT_LEAD, evenement.ancienStatut)} →{" "}
              <Box component="strong">{libelle(LIBELLES_STATUT_LEAD, evenement.nouveauStatut)}</Box>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formaterHorodatage(evenement.createdAt)}
              {evenement.auteur !== "" ? ` — ${evenement.auteur}` : ""}
            </Typography>
          </Box>
        </Box>
      ))}

      {/* L'arrivée de la demande clôt la liste par le bas : c'est le premier
          événement de sa vie, et l'historique se lit du plus récent au plus
          ancien une fois la fiche ouverte. */}
      <Divider />
      <Typography variant="caption" color="text.secondary">
        Demande reçue le {formaterHorodatage(createdAt)}
      </Typography>
    </Stack>
  );
}
